import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import { Eye, EyeOff, Shield, Lock, Unlock, RefreshCw, Save, LogOut, Key, AlertTriangle, CheckCircle, Loader2, ChevronRight, Zap } from 'lucide-react';
import ResetConfirmDialog from '@/components/ResetConfirmDialog';
import ResetProgressModal from '@/components/ResetProgressModal';
import { determineResetLevel, getResetDescription, performFullReset, logConfigChange } from '@/lib/resetUtils';
import { api } from '@/api/apiClient';

// ─── Crypto Helpers ─────────────────────────────────────────────────────────

function generateSalt() {
  return CryptoJS.lib.WordArray.random(128 / 8).toString();
}

function hashPassword(password, salt) {
  return CryptoJS.SHA256(password + salt).toString();
}

function deriveKey(masterPassword, salt) {
  return CryptoJS.PBKDF2(masterPassword, salt, { keySize: 256 / 32, iterations: 10000 }).toString();
}

function encryptConfig(config, masterPassword) {
  const salt = generateSalt();
  const key = deriveKey(masterPassword, salt);
  const data = CryptoJS.AES.encrypt(JSON.stringify(config), key).toString();
  return { data, salt };
}

function decryptConfig(encryptedData, masterPassword) {
  const key = deriveKey(masterPassword, encryptedData.salt);
  const bytes = CryptoJS.AES.decrypt(encryptedData.data, key);
  return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
}

// ─── Storage Helpers ─────────────────────────────────────────────────────────

const LS = {
  get: (k) => localStorage.getItem(k),
  set: (k, v) => localStorage.setItem(k, typeof v === 'string' ? v : JSON.stringify(v)),
  del: (k) => localStorage.removeItem(k),
};

const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 60 * 60 * 1000;
const AUTO_LOGOUT_MS = 5 * 60 * 1000;

function checkLockoutStatus() {
  const until = LS.get('admin_lockout_until');
  if (!until) return null;
  const exp = parseInt(until);
  if (Date.now() < exp) return exp;
  LS.del('admin_lockout_until');
  LS.del('admin_failed_attempts');
  return null;
}

function recordFailedAttempt() {
  const cur = parseInt(LS.get('admin_failed_attempts') || '0') + 1;
  if (cur >= MAX_ATTEMPTS) {
    LS.set('admin_lockout_until', String(Date.now() + LOCKOUT_MS));
    LS.del('admin_failed_attempts');
    return 0;
  }
  LS.set('admin_failed_attempts', String(cur));
  return MAX_ATTEMPTS - cur;
}

function clearFailedAttempts() {
  LS.del('admin_failed_attempts');
}

// ─── COMPONENTS ─────────────────────────────────────────────────────────────

function PinInput({ label, value, onChange, onSubmit, error }) {
  return (
    <div className="space-y-1">
      {label && <p className="text-sm text-white/70 font-medium">{label}</p>}
      <div className="flex gap-2">
        <input
          type="password"
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onSubmit?.()}
          autoFocus
          placeholder="••••••••"
          className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 text-base focus:outline-none focus:border-white/50 transition-colors"
          dir="ltr"
        />
      </div>
      {error && <p className="text-red-300 text-xs">{error}</p>}
    </div>
  );
}

function CountdownTimer({ targetMs, onExpire }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, targetMs - Date.now()));

  useEffect(() => {
    const iv = setInterval(() => {
      const r = Math.max(0, targetMs - Date.now());
      setRemaining(r);
      if (r === 0) { clearInterval(iv); onExpire?.(); }
    }, 1000);
    return () => clearInterval(iv);
  }, [targetMs, onExpire]);

  const mins = String(Math.floor(remaining / 60000)).padStart(2, '0');
  const secs = String(Math.floor((remaining % 60000) / 1000)).padStart(2, '0');
  return <span className="font-mono">{mins}:{secs}</span>;
}

// ─── SCREENS ────────────────────────────────────────────────────────────────

function SetupScreen({ onComplete }) {
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSetup = () => {
    setError('');
    if (p1.length < 8) return setError('كلمة المرور الرئيسية يجب أن تكون 8 أحرف على الأقل');
    if (p2.length < 8) return setError('كلمة المرور الثانية يجب أن تكون 8 أحرف على الأقل');
    if (p1 === p2) return setError('يجب أن تكون كلمتا المرور مختلفتين');

    setLoading(true);
    const masterSalt = generateSalt();
    const confirmSalt = generateSalt();
    LS.set('admin_master_hash', hashPassword(p1, masterSalt));
    LS.set('admin_master_salt', masterSalt);
    LS.set('admin_confirm_hash', hashPassword(p2, confirmSalt));
    LS.set('admin_confirm_salt', confirmSalt);

    // Encrypt current (env-based) Odoo config as placeholder
    const defaultConfig = { serverUrl: '', database: '', username: '', apiKey: '' };
    const enc = encryptConfig(defaultConfig, p1);
    LS.set('odoo_config_encrypted', JSON.stringify(enc));

    setTimeout(() => { setLoading(false); onComplete(p1); }, 400);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-6" dir="rtl">
      <div className="flex-1 flex flex-col items-center justify-center max-w-sm mx-auto w-full">
        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-black text-white mb-1">إعداد الحماية الأولى</h1>
        <p className="text-white/50 text-sm mb-8 text-center">عيّن كلمتي مرور للوصول إلى الإعدادات الإدارية</p>

        <div className="w-full space-y-4">
          <div className="space-y-1.5">
            <p className="text-sm text-white/70 font-medium">كلمة المرور الرئيسية</p>
            <div className="relative">
              <input
                type={show1 ? 'text' : 'password'}
                value={p1}
                onChange={e => setP1(e.target.value)}
                placeholder="8 أحرف على الأقل"
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white/50 pr-12"
                dir="ltr"
              />
              <button type="button" onClick={() => setShow1(v => !v)} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
                {show1 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {p1.length > 0 && (
              <p className={`text-xs ${p1.length >= 8 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {p1.length >= 8 ? '✓ طول مناسب' : `${8 - p1.length} أحرف إضافية مطلوبة`}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <p className="text-sm text-white/70 font-medium">كلمة المرور الثانية (تأكيد)</p>
            <div className="relative">
              <input
                type={show2 ? 'text' : 'password'}
                value={p2}
                onChange={e => setP2(e.target.value)}
                placeholder="يجب أن تختلف عن الأولى"
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-white/50 pr-12"
                dir="ltr"
              />
              <button type="button" onClick={() => setShow2(v => !v)} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
                {show2 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {p2.length > 0 && p1 === p2 && (
              <p className="text-xs text-red-400">⚠️ يجب أن تكون مختلفة عن الأولى</p>
            )}
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
            <p className="text-amber-300 text-xs">⚠️ احفظ كلمات المرور في مكان آمن — لا يمكن استرجاعها إذا نسيتها</p>
          </div>

          <button
            onClick={handleSetup}
            disabled={loading || p1.length < 8 || p2.length < 8 || p1 === p2}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            حفظ وتفعيل الحماية
          </button>
        </div>
      </div>
    </div>
  );
}

function AuthScreen({ onSuccess, onCancel }) {
  const [step, setStep] = useState(1);
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [error, setError] = useState('');
  const [remaining, setRemaining] = useState(MAX_ATTEMPTS);
  const [lockoutExp, setLockoutExp] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const exp = checkLockoutStatus();
    if (exp) setLockoutExp(exp);
    const attempts = parseInt(LS.get('admin_failed_attempts') || '0');
    setRemaining(MAX_ATTEMPTS - attempts);
  }, []);

  const handleStep1 = () => {
    setError('');
    setLoading(true);
    const masterHash = LS.get('admin_master_hash');
    const masterSalt = LS.get('admin_master_salt');
    setTimeout(() => {
      if (hashPassword(p1, masterSalt) === masterHash) {
        setStep(2);
        setLoading(false);
      } else {
        const rem = recordFailedAttempt();
        if (rem === 0) {
          const exp = Date.now() + LOCKOUT_MS;
          setLockoutExp(exp);
          setError('تم قفل الوصول لمدة ساعة بسبب محاولات متكررة');
        } else {
          setError(`كلمة المرور غير صحيحة — محاولات متبقية: ${rem}`);
          setRemaining(rem);
        }
        setP1('');
        setLoading(false);
      }
    }, 400);
  };

  const handleStep2 = () => {
    setError('');
    setLoading(true);
    const confirmHash = LS.get('admin_confirm_hash');
    const confirmSalt = LS.get('admin_confirm_salt');
    setTimeout(() => {
      if (hashPassword(p2, confirmSalt) === confirmHash) {
        clearFailedAttempts();
        onSuccess(p1);
      } else {
        const rem = recordFailedAttempt();
        if (rem === 0) {
          const exp = Date.now() + LOCKOUT_MS;
          setLockoutExp(exp);
          setError('تم قفل الوصول لمدة ساعة');
        } else {
          setError(`كلمة المرور الثانية غير صحيحة — محاولات متبقية: ${rem}`);
          setRemaining(rem);
        }
        setP2('');
        setLoading(false);
      }
    }, 400);
  };

  if (lockoutExp) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-900 via-red-950 to-slate-900 items-center justify-center p-6" dir="rtl">
        <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mb-4">
          <AlertTriangle className="w-8 h-8 text-red-400" />
        </div>
        <h2 className="text-xl font-black text-white mb-2">الوصول مقفل</h2>
        <p className="text-white/50 text-sm text-center mb-6">بسبب محاولات خاطئة متكررة</p>
        <div className="bg-white/10 rounded-2xl px-8 py-4 text-center mb-8">
          <p className="text-white/50 text-xs mb-1">يُفتح الوصول بعد</p>
          <p className="text-3xl font-black text-red-300">
            <CountdownTimer targetMs={lockoutExp} onExpire={() => { setLockoutExp(null); }} />
          </p>
        </div>
        <button onClick={onCancel} className="text-white/40 text-sm hover:text-white/70 transition-colors">
          العودة للإعدادات
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 items-center justify-center p-6" dir="rtl">
      <div className="max-w-sm w-full">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center">
            {step === 1 ? <Lock className="w-8 h-8 text-white" /> : <Key className="w-8 h-8 text-amber-300" />}
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex justify-center gap-2 mb-6">
          <div className={`h-1.5 w-12 rounded-full transition-colors ${step >= 1 ? 'bg-blue-400' : 'bg-white/20'}`} />
          <div className={`h-1.5 w-12 rounded-full transition-colors ${step >= 2 ? 'bg-amber-400' : 'bg-white/20'}`} />
        </div>

        <h1 className="text-xl font-black text-white text-center mb-1">
          {step === 1 ? 'منطقة محمية' : 'تأكيد إضافي مطلوب'}
        </h1>
        <p className="text-white/50 text-sm text-center mb-8">
          {step === 1 ? 'أدخل كلمة المرور الرئيسية' : 'أدخل كلمة المرور الثانية'}
        </p>

        <div className="space-y-4">
          {step === 1 ? (
            <PinInput value={p1} onChange={setP1} onSubmit={handleStep1} error={null} />
          ) : (
            <PinInput value={p2} onChange={setP2} onSubmit={handleStep2} error={null} />
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {!error && remaining < MAX_ATTEMPTS && (
            <p className="text-amber-400/70 text-xs text-center">محاولات متبقية: {remaining}</p>
          )}

          <button
            onClick={step === 1 ? handleStep1 : handleStep2}
            disabled={loading || (step === 1 ? !p1 : !p2)}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : step === 1 ? 'دخول' : 'تأكيد'}
          </button>

          <button onClick={onCancel} className="w-full text-white/40 text-sm hover:text-white/70 transition-colors py-2">
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminPanel({ masterPassword, onLogout }) {
  const [config, setConfig] = useState({ serverUrl: '', database: '', username: '', apiKey: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(AUTO_LOGOUT_MS);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [newP1, setNewP1] = useState('');
  const [newP2, setNewP2] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [toast, setToast] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(null);
  const [resetProgress, setResetProgress] = useState({ visible: false, step: 0, total: 6, message: '', error: null, isComplete: false });
  const [fieldErrors, setFieldErrors] = useState({});
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const formRef = useRef(null);
  const autoLogoutMs = useRef(Date.now() + AUTO_LOGOUT_MS);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Load config
  useEffect(() => {
    try {
      const stored = LS.get('odoo_config_encrypted');
      if (stored) {
        const parsed = JSON.parse(stored);
        const dec = decryptConfig(parsed, masterPassword);
        if (dec) setConfig(dec);
      }
    } catch {
      // config not set yet
    }
  }, [masterPassword]);

  // Auto-logout countdown
  const resetTimer = useCallback(() => {
    autoLogoutMs.current = Date.now() + AUTO_LOGOUT_MS;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onLogout();
    }, AUTO_LOGOUT_MS);
  }, [onLogout]);

  useEffect(() => {
    resetTimer();
    const activity = () => resetTimer();
    document.addEventListener('mousemove', activity);
    document.addEventListener('keydown', activity);
    document.addEventListener('touchstart', activity);

    countdownRef.current = setInterval(() => {
      setTimeLeft(Math.max(0, autoLogoutMs.current - Date.now()));
    }, 1000);

    return () => {
      clearTimeout(timerRef.current);
      clearInterval(countdownRef.current);
      document.removeEventListener('mousemove', activity);
      document.removeEventListener('keydown', activity);
      document.removeEventListener('touchstart', activity);
    };
  }, [resetTimer]);

  // Sync DOM values with state (handles autofill)
  const syncFormState = () => {
    if (!formRef.current) return config;
    
    const updatedConfig = {
      serverUrl: (formRef.current.querySelector('[name="serverUrl"]')?.value || '').trim(),
      database: (formRef.current.querySelector('[name="database"]')?.value || '').trim(),
      username: (formRef.current.querySelector('[name="username"]')?.value || '').trim(),
      apiKey: (formRef.current.querySelector('[name="apiKey"]')?.value || '').trim(),
    };
    
    setConfig(updatedConfig);
    return updatedConfig;
  };

  // Validate all fields
  const validateForm = () => {
    const current = syncFormState();
    const errors = {};
    
    if (!current.serverUrl) {
      errors.serverUrl = '🌐 رابط الخادم مطلوب';
    }
    if (!current.database) {
      errors.database = '💾 اسم قاعدة البيانات مطلوب';
    }
    if (!current.username) {
      errors.username = '👤 اسم المستخدم مطلوب';
    }
    if (!current.apiKey) {
      errors.apiKey = '🔑 كلمة المرور / API Key مطلوبة';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTest = async () => {
    if (!validateForm()) return;
    
    setTesting(true);
    setTestResult(null);
    const current = syncFormState();
    
    try {
      // Test by hitting Odoo's session endpoint on the configured server.
      // We don't persist this auth — it's purely a connectivity probe.
      const url = current.serverUrl.replace(/\/$/, '') + '/web/session/authenticate';
      const r = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          params: {
            db: current.database,
            login: current.username,
            password: current.apiKey,
          },
        }),
      });
      const json = await r.json().catch(() => ({}));
      if (json?.result?.uid) {
        setTestResult({ ok: true, msg: '✅ نجح الاتصال!' });
        showToast('نجح الاتصال بـ Odoo');
      } else {
        setTestResult({
          ok: false,
          msg: json?.error?.data?.message || json?.error?.message || 'فشل الاتصال — تحقق من البيانات',
        });
      }
    } catch (e) {
      setTestResult({ ok: false, msg: 'فشل الاتصال: ' + e.message });
    }
    setTesting(false);
  };

  const handleSaveClick = () => {
    if (!validateForm()) return;
    
    setTesting(true);
    const current = syncFormState();
    
    // First test with new values — same probe as handleTest above
    const url = current.serverUrl.replace(/\/$/, '') + '/web/session/authenticate';
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        params: {
          db: current.database,
          login: current.username,
          password: current.apiKey,
        },
      }),
    })
      .then(r => r.json().catch(() => ({})))
      .then(json => {
        if (json?.result?.uid) {
          // Test passed, show reset confirmation
          try {
            const stored = LS.get('odoo_config_encrypted');
            const oldConfig = stored ? decryptConfig(JSON.parse(stored), masterPassword) : null;
            const level = determineResetLevel(oldConfig, current);

            // Count pending items
            const pendingOrders = JSON.parse(LS.get('pending_orders') || '[]').length;
            const pendingPayments = JSON.parse(LS.get('pending_payments') || '[]').length;
            const pendingVisits = JSON.parse(LS.get('pending_visits') || '[]').length;
            const pendingCount = pendingOrders + pendingPayments + pendingVisits;

            setShowResetConfirm({ level, pendingCount });
            logConfigChange(oldConfig, current, level);
          } catch (e) {
            setShowResetConfirm({ level: 'FULL', pendingCount: 0 });
            logConfigChange(null, current, 'FULL');
          }
        } else {
          setTestResult({
            ok: false,
            msg: json?.error?.data?.message || json?.error?.message || 'فشل الاتصال',
          });
        }
        setTesting(false);
      })
      .catch(e => {
        setTestResult({ ok: false, msg: 'فشل الاتصال: ' + e.message });
        setTesting(false);
      });
  };

  const handleResetConfirm = async () => {
    setResetProgress({ visible: true, step: 0, total: 6, message: 'جاري التحضير...', error: null, isComplete: false });
    
    // Expose encryption function globally for reset utils
    window.__encryptConfig = encryptConfig;
    window.__resetProgress = (step, total, msg) => {
      setResetProgress(p => ({ ...p, step, message: msg }));
    };

    try {
      await performFullReset(config, masterPassword);
      setTimeout(() => {
        setResetProgress(p => ({ ...p, isComplete: true }));
      }, 1000);
    } catch (e) {
      setResetProgress(p => ({ ...p, error: 'حدث خطأ: ' + e.message }));
    }
  };

  const handleChangePwds = () => {
    setPwdError('');
    if (newP1.length < 8) return setPwdError('كلمة المرور 8 أحرف على الأقل');
    if (newP2.length < 8) return setPwdError('كلمة المرور الثانية 8 أحرف على الأقل');
    if (newP1 === newP2) return setPwdError('يجب أن تكون مختلفتين');

    const s1 = generateSalt(), s2 = generateSalt();
    LS.set('admin_master_hash', hashPassword(newP1, s1));
    LS.set('admin_master_salt', s1);
    LS.set('admin_confirm_hash', hashPassword(newP2, s2));
    LS.set('admin_confirm_salt', s2);

    // Re-encrypt config with new master
    try {
      const stored = LS.get('odoo_config_encrypted');
      if (stored) {
        const enc = encryptConfig(config, newP1);
        LS.set('odoo_config_encrypted', JSON.stringify(enc));
      }
    } catch {}

    setNewP1(''); setNewP2('');
    setShowChangePwd(false);
    showToast('تم تغيير كلمات المرور');
    onLogout(); // force re-auth with new passwords
  };

  const mins = String(Math.floor(timeLeft / 60000)).padStart(2, '0');
  const secs = String(Math.floor((timeLeft % 60000) / 1000)).padStart(2, '0');

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900" dir="rtl">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-2xl shadow-2xl text-sm font-bold flex items-center gap-2 transition-all
          ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`}>
          {toast.type === 'error' ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-emerald-500/20 rounded-xl flex items-center justify-center">
            <Unlock className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <p className="text-white font-bold text-sm">وضع المسؤول</p>
            <p className="text-white/40 text-xs">إعدادات Odoo</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-white/40 text-xs font-mono">⏱ {mins}:{secs}</p>
          <button onClick={onLogout} className="flex items-center gap-1 text-white/50 text-xs hover:text-white/80 transition-colors">
            <LogOut className="w-3.5 h-3.5" />
            خروج آمن
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-8">

        {/* Warning */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl px-4 py-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-amber-300 text-xs">التغييرات هنا تؤثر على اتصال جميع المستخدمين بـ Odoo</p>
        </div>

        {/* Config Form */}
        <form ref={formRef} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-white font-bold text-sm">⚙️ إعدادات الاتصال بـ Odoo</p>
          </div>
          <div className="p-4 space-y-4">
            {[
              { key: 'serverUrl', label: '🌐 رابط الخادم', placeholder: 'https://yourodoo.com', type: 'url' },
              { key: 'database', label: '💾 اسم قاعدة البيانات', placeholder: 'production_db', type: 'text' },
              { key: 'username', label: '👤 اسم المستخدم', placeholder: 'admin', type: 'text' },
            ].map(field => (
              <div key={field.key} className="space-y-1.5">
                <p className="text-white/70 text-sm font-medium">{field.label}</p>
                <input
                  name={field.key}
                  type={field.type}
                  value={config[field.key]}
                  onChange={e => {
                    setConfig(c => ({ ...c, [field.key]: e.target.value }));
                    setFieldErrors(prev => ({ ...prev, [field.key]: '' }));
                  }}
                  onBlur={syncFormState}
                  placeholder={field.placeholder}
                  className={`w-full bg-white/10 border rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none text-sm transition-colors ${
                    fieldErrors[field.key]
                      ? 'border-red-500 bg-red-500/5 focus:border-red-500/70'
                      : 'border-white/20 focus:border-white/50'
                  }`}
                  dir="ltr"
                />
                {fieldErrors[field.key] && (
                  <p className="text-red-400 text-xs mt-1">{fieldErrors[field.key]}</p>
                )}
              </div>
            ))}

            <div className="space-y-1.5">
              <p className="text-white/70 text-sm font-medium">🔑 كلمة المرور / API Key</p>
              <div className="relative">
                <input
                  name="apiKey"
                  type={showPassword ? 'text' : 'password'}
                  value={config.apiKey}
                  onChange={e => {
                    setConfig(c => ({ ...c, apiKey: e.target.value }));
                    setFieldErrors(prev => ({ ...prev, apiKey: '' }));
                  }}
                  onBlur={syncFormState}
                  placeholder="••••••••••••••••"
                  autoComplete="new-password"
                  className={`w-full bg-white/10 border rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none pr-12 text-sm transition-colors ${
                    fieldErrors.apiKey
                      ? 'border-red-500 bg-red-500/5 focus:border-red-500/70'
                      : 'border-white/20 focus:border-white/50'
                  }`}
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {fieldErrors.apiKey && (
                <p className="text-red-400 text-xs mt-1">{fieldErrors.apiKey}</p>
              )}
            </div>
          </div>
        </form>

        {/* Test Result */}
        {testResult && (
          <div className={`rounded-2xl px-4 py-3 flex items-center gap-2 ${testResult.ok ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            {testResult.ok ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
            <p className={`text-sm font-semibold ${testResult.ok ? 'text-emerald-300' : 'text-red-300'}`}>{testResult.msg}</p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleTest}
            disabled={testing}
            className="w-full bg-blue-600/80 hover:bg-blue-600 disabled:opacity-50 text-white font-bold py-3.5 rounded-2xl transition-colors flex items-center justify-center gap-2"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            اختبار الاتصال
          </button>

          <button
            onClick={handleSaveClick}
            disabled={testing}
            className="w-full bg-emerald-600/80 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold py-3.5 rounded-2xl transition-colors flex items-center justify-center gap-2"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            حفظ التغييرات
          </button>

          <button
            onClick={onLogout}
            className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 font-bold py-3.5 rounded-2xl transition-colors flex items-center justify-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            خروج آمن
          </button>
        </div>

        {/* Audit Log */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="px-4 py-3">
            <p className="text-white font-bold text-sm">📋 سجل التغييرات</p>
          </div>
          <div className="px-4 pb-3 space-y-2 max-h-40 overflow-y-auto">
            {(() => {
              const log = JSON.parse(localStorage.getItem('admin_audit_log') || '[]');
              return log.length > 0 ? (
                log.slice().reverse().slice(0, 5).map((entry, i) => (
                  <div key={i} className="text-xs border-t border-white/10 pt-2 first:border-0 first:pt-0">
                    <p className="text-white/70 font-medium">{new Date(entry.timestamp).toLocaleString('ar-EG')}</p>
                    <p className="text-white/50 mt-0.5">
                      {entry.resetLevel === 'FULL' && '🔄 إعادة تهيئة كاملة'}
                      {entry.resetLevel === 'SOFT' && '🔄 إعادة تهيئة متوسطة'}
                      {entry.resetLevel === 'CACHE' && '⚡ مسح Cache فقط'}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-white/40 text-xs py-2">لا توجد تغييرات سابقة</p>
              );
            })()}
          </div>
        </div>

        {/* Manual Reset */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowResetConfirm('FULL')}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-white/50" />
              <span className="text-white/70 text-sm font-medium">إعادة تهيئة يدوية</span>
            </div>
            <ChevronRight className="w-4 h-4 text-white/30" />
          </button>
          <div className="px-4 pb-3 border-t border-white/10 pt-3">
            <p className="text-white/50 text-xs mb-3">
              استخدم هذا الخيار لإعادة مزامنة البيانات بدون تغيير إعدادات الخادم
            </p>
          </div>
        </div>

        {/* Change Passwords */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowChangePwd(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-white/50" />
              <span className="text-white/70 text-sm font-medium">تغيير كلمات المرور</span>
            </div>
            <ChevronRight className={`w-4 h-4 text-white/30 transition-transform ${showChangePwd ? 'rotate-90' : ''}`} />
          </button>

          {showChangePwd && (
            <div className="px-4 pb-4 space-y-3 border-t border-white/10 pt-3">
              {[
                { val: newP1, set: setNewP1, label: 'كلمة المرور الرئيسية الجديدة' },
                { val: newP2, set: setNewP2, label: 'كلمة المرور الثانية الجديدة' },
              ].map((f, i) => (
                <div key={i} className="space-y-1">
                  <p className="text-white/60 text-xs">{f.label}</p>
                  <input
                    type="password"
                    value={f.val}
                    onChange={e => f.set(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:border-white/50 text-sm"
                    dir="ltr"
                    placeholder="••••••••"
                  />
                </div>
              ))}
              {pwdError && <p className="text-red-400 text-xs">{pwdError}</p>}
              <button
                onClick={handleChangePwds}
                disabled={!newP1 || !newP2}
                className="w-full bg-amber-600/80 hover:bg-amber-600 disabled:opacity-40 text-white font-bold py-2.5 rounded-xl transition-colors text-sm"
              >
                تطبيق كلمات المرور الجديدة
              </button>
            </div>
          )}
        </div>

      </div>

      {/* Reset Confirm Dialog */}
      {showResetConfirm && (
        <ResetConfirmDialog
          level={showResetConfirm.level || showResetConfirm}
          pendingCount={showResetConfirm.pendingCount}
          onConfirm={handleResetConfirm}
          onCancel={() => setShowResetConfirm(null)}
          isLoading={resetProgress.visible}
        />
      )}

      {/* Reset Progress Modal */}
      <ResetProgressModal
        isVisible={resetProgress.visible}
        step={resetProgress.step}
        totalSteps={resetProgress.total}
        message={resetProgress.message}
        error={resetProgress.error}
        isComplete={resetProgress.isComplete}
      />
    </div>
  );
}

// ─── MAIN PAGE ───────────────────────────────────────────────────────────────

export default function OdooAdminConfig() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState(null); // null = loading, 'setup', 'auth', 'admin'
  const [masterPassword, setMasterPassword] = useState(null);

  useEffect(() => {
    const lockExp = checkLockoutStatus();
    const hasSetup = LS.get('admin_master_hash');
    if (!hasSetup) setScreen('setup');
    else if (lockExp) setScreen('auth');
    else setScreen('auth');
  }, []);

  const handleSetupComplete = (pwd) => {
    setMasterPassword(pwd);
    setScreen('admin');
  };

  const handleAuthSuccess = (pwd) => {
    setMasterPassword(pwd);
    setScreen('admin');
  };

  const handleLogout = () => {
    setMasterPassword(null);
    navigate('/settings');
  };

  if (screen === null) return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900">
      <Loader2 className="w-8 h-8 text-white animate-spin" />
    </div>
  );

  if (screen === 'setup') return <SetupScreen onComplete={handleSetupComplete} />;
  if (screen === 'auth') return <AuthScreen onSuccess={handleAuthSuccess} onCancel={() => navigate('/settings')} />;
  if (screen === 'admin') return <AdminPanel masterPassword={masterPassword} onLogout={handleLogout} />;
  return null;
}